/**
 * Utility functions for notification validation and filtering
 */

/**
 * Type definition for a notification object
 */
export interface Notification {
  id: string | number;
  notification_message: string;
  profile_pic?: string;
  course_id?: string | number;
  is_accepted?: boolean;
  time?: string;
  datetime?: string;
  created_at?: string;
  [key: string]: any; // Allow additional properties
}

/**
 * Validates if a notification object has all required fields
 * @param {Notification} notification - The notification object to validate
 * @returns {boolean} - True if notification is valid, false otherwise
 */
export const isValidNotification = (notification: any): notification is Notification => {
  const isValid = (
    notification &&
    notification.id &&
    notification.notification_message &&
    typeof notification.notification_message === 'string' &&
    notification.notification_message.trim() !== ""
  );
  
  // Debug log for invalid notifications
  if (notification && !isValid) {
    console.warn('Invalid notification detected:', {
      hasNotification: !!notification,
      hasId: !!notification.id,
      hasMessage: !!notification.notification_message,
      messageType: typeof notification.notification_message,
      messageTrimmed: notification.notification_message?.trim() || '',
      notification
    });
  }
  
  return isValid;
};

/**
 * Filters an array of notifications to only include valid ones
 * @param {any[]} notifications - Array of notification objects
 * @returns {Notification[]} - Array containing only valid notifications
 */
export const filterValidNotifications = (notifications: any[]): Notification[] => {
  if (!Array.isArray(notifications)) {
    return [];
  }
  
  return notifications.filter(isValidNotification);
};

/**
 * Removes duplicate notifications based on ID
 * @param {Notification[]} notifications - Array of notification objects
 * @returns {Notification[]} - Array with duplicate notifications removed
 */
export const removeDuplicateNotifications = (notifications: Notification[]): Notification[] => {
  if (!Array.isArray(notifications)) {
    return [];
  }
  
  const seen = new Set();
  return notifications.filter(notification => {
    if (notification && notification.id) {
      if (seen.has(notification.id)) {
        return false;
      }
      seen.add(notification.id);
      return true;
    }
    return false;
  });
};

/**
 * Combines and validates multiple notification arrays
 * @param {...Notification[][]} notificationArrays - Multiple arrays of notifications
 * @returns {Notification[]} - Combined array of valid, unique notifications
 */
export const combineValidNotifications = (...notificationArrays: any[][]): Notification[] => {
  const allNotifications = notificationArrays.flat().filter(Boolean);
  const validNotifications = filterValidNotifications(allNotifications);
  return removeDuplicateNotifications(validNotifications);
};
